import {combineLatest} from 'rxjs';
import {filter, map, shareReplay, startWith, throttleTime} from 'rxjs/operators';
import {numUtil, plus} from '@/helper/num';
import {wwData$} from './worker-data';
import {isUtaSpot$} from './uta-state';
import {totalFiatAssets$} from './fiat-account';
import type {Observable} from 'rxjs';
import type {FundAccount, Assets} from '@px/ws-data';

type FundAssets = Assets & {money: string};

const BTC_PRECISION = 8;
const USD_PRECISION = 2;

export const fundAssets$: Observable<FundAssets> = wwData$.pipe(
  filter((o) => o.type === 'fund-assets'),
  map((o) => o.data.assets as FundAssets),
  startWith(initAssets()),
  shareReplay(1)
);

const innerFundAccounts$ = wwData$.pipe(
  filter((o) => o.type === 'fund-accounts'),
  map((o) => o.data.accounts as FundAccount[]),
  throttleTime(50),
  shareReplay(1)
);

export const fundAccounts$: Observable<FundAccount[]> = combineLatest({fundAccounts: innerFundAccounts$, isUtaSpot: isUtaSpot$}).pipe(
  filter(({isUtaSpot}) => isUtaSpot),
  map(({fundAccounts}) => fundAccounts),
  startWith([]),
  shareReplay(1)
);

const innerFundScaleAccounts$ = wwData$.pipe(
  filter((o) => o.type === 'fund-scale-accounts'),
  map((o) => o.data.accounts as FundAccount[]),
  throttleTime(50),
  shareReplay(1)
);

export const fundScaleAccounts$: Observable<FundAccount[]> = combineLatest({fundAccounts: innerFundScaleAccounts$, isUtaSpot: isUtaSpot$}).pipe(
  filter(({isUtaSpot}) => isUtaSpot),
  map(({fundAccounts}) => fundAccounts),
  startWith([]),
  shareReplay(1)
);

const innerFundInvalidAccounts$ = wwData$.pipe(
  filter((o) => o.type === 'fund-invalid-accounts'),
  map((o) => o.data.accounts as FundAccount[]),
  throttleTime(50),
  shareReplay(1)
);

export const fundInvalidAccounts$: Observable<FundAccount[]> = combineLatest({fundAccounts: innerFundInvalidAccounts$, isUtaSpot: isUtaSpot$}).pipe(
  filter(({isUtaSpot}) => isUtaSpot),
  map(({fundAccounts}) => fundAccounts),
  startWith([]),
  shareReplay(1)
);

const innerFundInvalidScaleAccounts$ = wwData$.pipe(
  filter((o) => o.type === 'fund-invalid-scale-accounts'),
  map((o) => o.data.accounts as FundAccount[]),
  throttleTime(50),
  shareReplay(1)
);

export const fundInvalidScaleAccounts$: Observable<FundAccount[]> = combineLatest({fundAccounts: innerFundInvalidScaleAccounts$, isUtaSpot: isUtaSpot$}).pipe(
  filter(({isUtaSpot}) => isUtaSpot),
  map(({fundAccounts}) => fundAccounts),
  startWith([]),
  shareReplay(1)
);

export const fundAccountGroupAssets$ = combineLatest({fundAssets: fundAssets$, fiatAssets: totalFiatAssets$}).pipe(
  map(updateAssets),
  startWith({
    isInit: true,
    btcEv: 0,
    btc: (0).toFixed(BTC_PRECISION),
    usdEv: 0,
    usd: (0).toFixed(USD_PRECISION),
    money: (0).toFixed(USD_PRECISION),
    usdtEv: 0,
    usdt: (0).toFixed(USD_PRECISION),
  })
);

function updateAssets({fundAssets, fiatAssets}) {
  return {
    btcEv: plus(fundAssets.btcEv, fiatAssets.btcEv),
    btc: plusValStr(fundAssets.btc, fiatAssets.btc),
    usdEv: plus(fundAssets.usdEv, fiatAssets.usdEv),
    usd: plusValStr(fundAssets.usd, fiatAssets.usd),
    money: plusValStr(fundAssets.money, fiatAssets.money),
    usdtEv: plus(fundAssets.usdtEv, fiatAssets.usdtEv),
    usdt: plusValStr(fundAssets.usdt, fiatAssets.usdt),
  };
}

function plusValStr(val1, val2) {
  return numUtil.delimit(plus(val1.replaceAll(',', ''), val2.replaceAll(',', '')));
}

function initAssets() {
  return {
    btcEv: 0,
    btc: (0).toFixed(BTC_PRECISION),
    usdEv: 0,
    usd: (0).toFixed(USD_PRECISION),
    money: (0).toFixed(USD_PRECISION),
    usdtEv: 0,
    usdt: (0).toFixed(USD_PRECISION),
    bonusBtcEv: 0,
    bonusBtc: (0).toFixed(BTC_PRECISION),
    bonusUsdEv: 0,
    bonusUsd: (0).toFixed(USD_PRECISION),
    bonusMoney: (0).toFixed(USD_PRECISION),
    bonusUsdtEv: 0,
    bonusUsdt: (0).toFixed(USD_PRECISION),
  };
}
